import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import Loading from "../../Loading/Loading";
import useHelpContent from "../../../hooks/useHelpContent";
import { PaymentsHelpContentKey } from "../../../constants";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/**
 * Sanitises the CDN document and sends its external links to a new tab.
 *
 * The copy is authored outside the app and points at YouTube, the marketing
 * site and the payment terms, so following one in place would drop the
 * professional out of the web app mid-registration.
 *
 * The rewrite happens AFTER sanitising and only ever adds attributes, so it
 * cannot widen what DOMPurify allowed through. It is done here rather than with
 * DOMPurify.addHook because a hook is global and would silently change the
 * clinical records rendered by RecordCard too.
 *
 * In-page anchors (#section) are left alone — a new tab for those is nonsense.
 */
function sanitiseHelpHtml(html: string): string {
  const clean = DOMPurify.sanitize(html);
  const template = document.createElement("template");
  template.innerHTML = clean;

  template.content.querySelectorAll("a[href]").forEach(anchor => {
    const href = anchor.getAttribute("href") ?? "";
    if (!/^https?:\/\//i.test(href)) return;
    anchor.setAttribute("target", "_blank");
    // Without noopener the opened page can navigate this one (tabnabbing).
    anchor.setAttribute("rel", "noopener noreferrer");
  });

  return template.innerHTML;
}

/**
 * Renders a help document served by the CDN.
 *
 * The mobile app shows this copy inside a WebView, which isolates it. A browser
 * has no such boundary, so the HTML is sanitised before being injected.
 *
 * Nothing is rendered when the content fails to load: this is supporting copy
 * sitting alongside a screen, and an error banner over it would be noisier than
 * the help is worth. `explainWhenMissing` flips that for the cases where this is
 * the ONLY thing on screen — inside the help dialog, silence leaves the user
 * staring at an empty modal they deliberately opened. Not hypothetical:
 * `payments_at_update` answers 404 on the CDN today (as it does for the mobile).
 */
export default function HelpContent({
  linkName,
  explainWhenMissing = false
}: {
  linkName: PaymentsHelpContentKey;
  explainWhenMissing?: boolean;
}) {
  const { t } = useTranslation();
  const { html, loading, error } = useHelpContent(linkName);
  const safeHtml = useMemo(() => (html ? sanitiseHelpHtml(html) : ""), [html]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <Loading />
      </Box>
    );
  }

  if (error || !html) {
    if (!explainWhenMissing) return null;
    return (
      <Typography
        sx={{ ...typography.body.l, color: semanticColors.text.secondary }}
      >
        {t("paymentConfigurations.helpUnavailable")}
      </Typography>
    );
  }

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: safeHtml }}
      sx={{
        ...typography.body.l,
        color: semanticColors.text.primary,
        "& img": { maxWidth: "100%" }
      }}
    />
  );
}
